import { useEffect, useRef } from "react";
import { useKeyboardHeight } from "../../../hooks/use-keyboard-height";

// Keeps the end of the answer visible when the soft keyboard opens on the chat
// surfaces (iOS/Android). The composer is `position: fixed` and rises above the
// keyboard (KeyboardAvoidingView), while the scroller is `h-dvh` and does NOT
// shrink with the keyboard (the WKWebView is pinned by `useKeyboardManaged`).
// Without help the transcript's static bottom padding is too small to scroll the
// answer's tail past the raised composer, so the end of the answer stays hidden
// behind the keyboard. Padding + a re-pin to the bottom on every keyboard
// transition:
//   1. Pad the scroller's bottom by the keyboard height so the transcript can
//      scroll far enough for its last line to clear the raised composer.
//   2. Re-pin to the bottom whenever the keyboard state settles — both on open
//      (use the newly available room) AND on close. The close case is the
//      important one: the padding drops back to 0 and the scroller shrinks, but
//      iOS WKWebView does NOT reliably re-clamp an `overflow:auto` element's
//      `scrollTop` when its content shrinks. The view stays stuck below the new
//      max — the first turn scrolled out of reach above the header, with the
//      scroll gesture rubber-banding instead of revealing it. Reading
//      `scrollHeight` in `scrollToBottom` forces a reflow and `scrollTo` clamps
//      to the valid bottom, releasing the stuck state. `scrollToBottom` is
//      stable and the keyboard height is constant once settled, so this never
//      fights a user scrolling up to re-read earlier turns.
// Only a TRANSITION re-pins, never the mount: where a surface opens is its own
// choice (`useChatScrollAnchor`'s `initialToBottom`). A re-pin at mount took
// that choice away from the shared conversation, which opens on its first
// question: it landed on the end of the answer, and that scroll raised the
// signup sheet at once.
// Returns the bottom padding to apply to the scroll container's inline style.
export function useChatKeyboardScroll(scrollToBottom: () => void): {
  paddingBottom: string;
} {
  const { keyboardHeight, isKeyboardOpen } = useKeyboardHeight();
  // A closed keyboard has no height worth following: the visual viewport also
  // moves with a pinch zoom or a browser toolbar.
  const reserved = isKeyboardOpen ? keyboardHeight : 0;
  // The reserve the last effect saw, or null before the first one.
  const previousReserved = useRef<number | null>(null);

  useEffect(() => {
    const previous = previousReserved.current;
    previousReserved.current = reserved;
    if (previous === null || previous === reserved) return;
    scrollToBottom();
  }, [reserved, scrollToBottom]);

  return { paddingBottom: `${String(reserved)}px` };
}
